import { Autocomplete, Typography } from '@sravni/react-design-system';
import type { SelectOption } from '@sravni/react-design-system/dist/types/types/Select.types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useGetFullAddressAutocomplete } from 'shared/api/daData';

import styles from './DefaultAddressField.module.scss';
import { transformationAddressList, validationDadata } from './lib';

const { Text } = Typography;

type Props = {
  address: string;
  setAddress: (v: string) => void;
  placeholder: string;
  onSelectAddress: (v: string) => void;
  meetingError: string;
  setErrorAddress: (v: string) => void;
};

export const DefaultAddressField = ({
  placeholder,
  meetingError,
  onSelectAddress,
  address,
  setAddress,
  setErrorAddress,
}: Props) => {
  const [addressList, setAddressList] = useState<NonNullable<SelectOption[]>>([]);

  const [getAddressAutocomplete] = useGetFullAddressAutocomplete();

  const refAutocomplete = useRef<HTMLTextAreaElement>(null);
  const prevAddress = useRef<string>();
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      const { data } = await getAddressAutocomplete({ search: address }, true);

      data && setAddressList(transformationAddressList(data));
    }, 700);

    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [getAddressAutocomplete, address]);

  const handleSearch = useCallback(
    async (newAddress: string) => {
      setAddress(newAddress);
    },
    [setAddress],
  );

  const handleSelect = async (newAddress: string | number | Array<string | number>) => {
    const validAddress = newAddress?.toString();
    const { data: dadataAddresses } = await getAddressAutocomplete({ search: validAddress }, true);

    const { _errorMessage } = validationDadata(validAddress, dadataAddresses);

    if (_errorMessage) {
      setErrorAddress(_errorMessage);
      setAddress(validAddress);
      onSelectAddress('');

      return;
    }

    setErrorAddress('');
    setAddress(validAddress);

    if (prevAddress.current !== validAddress) {
      prevAddress.current = validAddress;

      onSelectAddress(validAddress);
    }
  };

  const onBlur = () => {
    handleSelect(address);
  };

  const error = meetingError;
  const options = addressList.length ? addressList : [];

  return (
    <div className={styles.wrapper}>
      <Autocomplete
        ref={refAutocomplete}
        placeholder={placeholder}
        invalid={Boolean(error)}
        onSelect={handleSelect}
        onSearch={handleSearch}
        onBlur={onBlur}
        options={options}
        value={address}
      />
      {error && <Text className="h-color-R100">{error}</Text>}
    </div>
  );
};
